<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { PlusCircle } from 'lucide-svelte';
  import { productsApi } from '$lib/api/products';
  import { getErrorMessage } from '$lib/api/client';
  import { isValidModelNumber, normalizeModelNumber, standardModelOptions } from '$lib/utils/modelNumbers';
  import ProductCategoryBadges from '$lib/components/ui/ProductCategoryBadges.svelte';

  const { disabled = false, onAdd }: { disabled?: boolean; onAdd: (productId: number) => void } = $props();

  let term = $state('');
  let open = $state(false);
  let highlighted = $state(-1);

  // Inline "add as new product" form, shown inside the dropdown.
  let addOpen = $state(false);
  let newName = $state('');
  let newCategory = $state('');
  let newPartNumber = $state('');
  let selectedModel = $state('');
  let addError = $state('');
  let saving = $state(false);

  const productsQuery = createQuery(() => ({
    queryKey: ['catalog-search', term],
    queryFn: () => productsApi.list({ q: term, per_page: 20 }),
    enabled: open
  }));

  function pick(id: number) {
    onAdd(id);
    term = '';
    open = false;
    highlighted = -1;
  }

  function scrollIntoView(index: number) {
    const el = document.getElementById(`catalog-option-${index}`);
    el?.scrollIntoView({ block: 'nearest' });
  }

  function onKeydown(e: KeyboardEvent) {
    if (addOpen) return;
    const results = productsQuery.data?.results ?? [];
    if (e.key === 'ArrowDown') {
      if (!results.length) return;
      e.preventDefault();
      highlighted = (highlighted + 1) % results.length;
      scrollIntoView(highlighted);
    } else if (e.key === 'ArrowUp') {
      if (!results.length) return;
      e.preventDefault();
      highlighted = (highlighted - 1 + results.length) % results.length;
      scrollIntoView(highlighted);
    } else if (e.key === 'Enter') {
      const pickIndex = highlighted >= 0 ? highlighted : 0;
      const target = results[pickIndex];
      if (target) {
        e.preventDefault();
        pick(target.id);
      }
    } else if (e.key === 'Escape') {
      open = false;
      term = '';
      highlighted = -1;
    }
  }

  function startAdd() {
    newName = term.trim();
    newCategory = '';
    newPartNumber = '';
    selectedModel = '';
    addError = '';
    addOpen = true;
  }

  function cancelAdd() {
    addOpen = false;
    addError = '';
  }

  async function submitAdd() {
    const name = newName.trim();
    if (!name) {
      addError = 'Name is required.';
      return;
    }
    const partNumber = normalizeModelNumber(newPartNumber);
    if (!isValidModelNumber(partNumber)) {
      addError = 'Use uppercase letters/numbers separated by hyphens; TEMP codes are not allowed.';
      return;
    }
    saving = true;
    addError = '';
    try {
      const product = await productsApi.upsert({
        name,
        part_number: partNumber,
        category: newCategory.trim() || undefined
      });
      addOpen = false;
      pick(product.id);
    } catch (err) {
      addError = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  function hasExactMatch(results: { name: string }[]): boolean {
    const needle = term.trim().toLowerCase();
    return results.some((p) => p.name.toLowerCase() === needle);
  }

  function chooseModel() {
    if (selectedModel === 'other') {
      newPartNumber = '';
    } else if (selectedModel) {
      newPartNumber = selectedModel;
    }
  }
</script>

<div class="relative px-5 py-3 border-t border-toyama-cloudy-gray bg-toyama-light-carbon flex items-center gap-2.5 rounded-b-[inherit]">
  <PlusCircle class="h-4 w-4 text-toyama-teal shrink-0" />
  <div class="relative flex-1 max-w-[380px]">
    <input
      class="input"
      placeholder="Add a product from the catalog…"
      {disabled}
      bind:value={term}
      role="combobox"
      aria-expanded={open}
      aria-controls="catalog-listbox"
      oninput={() => (highlighted = -1)}
      onfocus={() => (open = true)}
      onblur={() => setTimeout(() => { if (!addOpen) open = false; }, 150)}
      onkeydown={onKeydown}
    />
    {#if open}
      <div
        id="catalog-listbox"
        role="listbox"
        class="absolute z-10 top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-[var(--tym-white)] border border-toyama-cloudy-gray rounded-lg shadow-[var(--tym-shadow-3)]"
      >
        {#if productsQuery.isLoading}
          <div class="px-3 py-2.5 text-[12.5px] text-dark-gray">Searching&hellip;</div>
        {:else}
          {@const results = productsQuery.data?.results ?? []}
          {#if results.length === 0 && !addOpen}
            <div class="px-3 py-2.5 text-[12.5px] text-dark-gray">No matching products.</div>
          {/if}
          {#each results as p, i (p.id)}
            <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
            <button
              type="button"
              id="catalog-option-{i}"
              role="option"
              aria-selected={i === highlighted}
              class="block w-full text-left px-3 py-2 hover:bg-toyama-light-carbon text-[13px] {i === highlighted ? 'bg-toyama-light-carbon' : ''}"
              onmousedown={(e) => e.preventDefault()}
              onmouseenter={() => (highlighted = i)}
              onclick={() => pick(p.id)}
            >
              <span class="text-navy font-medium">{p.name}</span>
              <span class="text-dark-gray font-mono text-[11.5px] ml-1.5">{p.part_number}</span>
              <ProductCategoryBadges product={p} />
            </button>
          {/each}
          {#if addOpen}
            <div class="px-3 py-2.5 border-t border-toyama-cloudy-gray space-y-1.5">
              <input
                class="input text-[13px]"
                placeholder="Product name"
                bind:value={newName}
                onmousedown={(e) => e.stopPropagation()}
              />
              <input
                class="input text-[13px]"
                placeholder="Category (optional)"
                bind:value={newCategory}
                onmousedown={(e) => e.stopPropagation()}
              />
              <select class="input text-[13px]" bind:value={selectedModel} onchange={chooseModel} onmousedown={(e) => e.stopPropagation()}>
                <option value="">Choose a standard model number…</option>
                {#each standardModelOptions as option (option.value)}
                  <option value={option.value}>{option.label} ({option.value})</option>
                {/each}
                <option value="other">Other approved model number…</option>
              </select>
              <input
                class="input text-[13px]"
                placeholder="Model number (required)"
                bind:value={newPartNumber}
                oninput={() => (newPartNumber = normalizeModelNumber(newPartNumber))}
                onmousedown={(e) => e.stopPropagation()}
              />
              <p class="text-[11px] text-dark-gray">Product family – function – capacity/type – protocol (when applicable). Use uppercase letters/numbers with single hyphens; TEMP codes are not allowed.</p>
              {#if newPartNumber && !isValidModelNumber(newPartNumber)}
                <div class="text-[11.5px] text-red-600">Use uppercase letters/numbers separated by hyphens; TEMP codes are not allowed.</div>
              {/if}
              {#if addError}
                <div class="text-[11.5px] text-red-600">{addError}</div>
              {/if}
              <div class="flex items-center gap-2 pt-0.5">
                <button
                  type="button"
                  class="btn-primary text-[12px] px-2.5 py-1"
                  disabled={saving || !newName.trim() || !isValidModelNumber(newPartNumber)}
                  onmousedown={(e) => e.preventDefault()}
                  onclick={submitAdd}
                >
                  {saving ? 'Adding…' : 'Add'}
                </button>
                <button
                  type="button"
                  class="text-[12px] text-dark-gray hover:text-navy"
                  onmousedown={(e) => e.preventDefault()}
                  onclick={cancelAdd}
                >
                  Cancel
                </button>
              </div>
            </div>
          {:else if term.trim() && !hasExactMatch(results)}
            <button
              type="button"
              class="block w-full text-left px-3 py-2 hover:bg-toyama-light-carbon text-[13px] text-toyama-teal border-t border-toyama-cloudy-gray"
              onmousedown={(e) => e.preventDefault()}
              onclick={startAdd}
            >
              + Add "{term.trim()}" as a new product
            </button>
          {/if}
        {/if}
      </div>
    {/if}
  </div>
</div>
